/**
 * TwinCoverFlow — the room's twins as the home page's cover flow.
 *
 * Up to three cards show at once: the active twin in front, raised and full
 * size, its neighbours to each side, smaller and turned slightly away. Arrows
 * (and a click on a side card) rotate the flow, wrapping around, so every
 * twin of the room can come to the front even with two or three twins. One
 * twin is a single card with no arrows.
 *
 * Each card is the twin's portrait with a dark foot gradient carrying the
 * first name (headline font) and whatever the host renders under it (segment
 * tags). `renderCard` wraps a card in its profile (hover card on desktop,
 * sheet on phones), the same contract as TwinPosterRow. Phones get a smaller
 * stage. Spring moves follow the home page; reduced motion snaps.
 */

import { ReactElement, ReactNode, forwardRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import { UserAvatar } from '@/components/ui/UserAvatar'
import { cn } from '@/lib/utils'
import { getUserInitials } from '@/lib/utils/avatar'

/** Card size and side-slot distance: the home page flow at 70%, so the stage leaves room for the composer. */
const CARD_WIDTH_PX = 168
const CARD_HEIGHT_PX = 190
const SIDE_OFFSET_PX = 164
const ACTIVE_LIFT_PX = 18
const SIDE_SCALE = 0.86
const SIDE_ROTATE_DEG = 4
const SPRING = { type: 'spring' as const, stiffness: 280, damping: 28, mass: 0.9 }

export interface TwinCoverFlowTwin {
  id: string
  name: string
  imageUrl?: string | null
}

interface TwinCoverFlowProps {
  twins: Array<TwinCoverFlowTwin>
  /** Accessible name of the flow. */
  label?: string
  /** Wraps a twin's card (e.g. its profile hover card). */
  renderCard?: (twinIndex: number, card: ReactElement) => ReactNode
  /** Content under a twin's first name, inside the card (e.g. segment tags). */
  renderBelowName?: (twinIndex: number) => ReactNode
  previousLabel: string
  nextLabel: string
  className?: string
}

interface SlotTarget {
  x: number
  y: number
  scale: number
  rotate: number
  opacity: number
}

/** The card shows the first name only; the full name stays the card's accessible label. */
export function getFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name
}

export function wrapIndex(index: number, length: number): number {
  if (length === 0) {
    return 0
  }
  return ((index % length) + length) % length
}

/** Shortest signed distance from the active card, so cards take the nearest way round. */
export function getFlowOffset(index: number, activeIndex: number, length: number): number {
  if (length === 0) {
    return 0
  }
  let offset = index - activeIndex
  if (offset > length / 2) {
    offset -= length
  }
  if (offset < -length / 2) {
    offset += length
  }
  // Two twins: the other card always sits on the right.
  if (length === 2 && offset < 0) {
    offset = 1
  }
  return offset
}

/** Where a card sits for its offset; a pair is centered as a pair (active left, the other right). */
export function getSlotTarget(offset: number, count: number): SlotTarget {
  const distance = Math.abs(offset)
  const sign = Math.sign(offset)
  const pairShift = count === 2 ? -SIDE_OFFSET_PX / 2 : 0
  if (distance === 0) {
    return { x: pairShift, y: -ACTIVE_LIFT_PX, scale: 1, rotate: 0, opacity: 1 }
  }
  if (distance === 1) {
    return {
      x: pairShift + sign * SIDE_OFFSET_PX,
      y: 0,
      scale: SIDE_SCALE,
      rotate: sign * SIDE_ROTATE_DEG,
      opacity: 0.92,
    }
  }
  return { x: sign * SIDE_OFFSET_PX * 2, y: 0, scale: 0.7, rotate: sign * SIDE_ROTATE_DEG * 2, opacity: 0 }
}

export function TwinCoverFlow(props: TwinCoverFlowProps) {
  const { twins, label, renderCard, renderBelowName, previousLabel, nextLabel, className } = props

  const [activeIndex, setActiveIndex] = useState(0)
  const prefersReducedMotion = useReducedMotion()

  const count = twins.length
  const canRotate = count > 1
  const current = wrapIndex(activeIndex, count)

  const goPrevious = () => setActiveIndex((index) => wrapIndex(index - 1, count))
  const goNext = () => setActiveIndex((index) => wrapIndex(index + 1, count))

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      className={cn('flex w-full justify-center', className)}
      data-testid="twin-cover-flow"
    >
      {/* Phones get a smaller stage; the slot math stays the same. */}
      <div className="relative h-[212px] w-[560px] max-w-full origin-top scale-[0.85] sm:scale-100">
        <div className="absolute inset-0">
          {twins.map((twin, index) => {
            const offset = getFlowOffset(index, current, count)
            const isActive = offset === 0
            const isVisible = Math.abs(offset) <= 1
            const target = getSlotTarget(offset, count)

            const card = (
              <CoverCard
                twin={twin}
                index={index}
                isActive={isActive}
                belowName={renderBelowName?.(index)}
                onSelect={() => setActiveIndex(index)}
              />
            )

            return (
              <motion.div
                key={twin.id}
                initial={false}
                animate={{ x: target.x, y: target.y, scale: target.scale, rotate: target.rotate, opacity: target.opacity }}
                transition={prefersReducedMotion ? { duration: 0 } : SPRING}
                className="absolute bottom-0 left-1/2"
                style={{
                  marginLeft: -CARD_WIDTH_PX / 2,
                  zIndex: isActive ? 20 : isVisible ? 10 : 0,
                  pointerEvents: isVisible ? 'auto' : 'none',
                }}
                aria-hidden={isVisible ? undefined : true}
                data-testid={`twin-cover-flow-slot-${index}`}
                data-offset={offset}
              >
                {renderCard ? renderCard(index, card) : card}
              </motion.div>
            )
          })}
        </div>

        {canRotate && (
          <>
            <FlowArrow direction="left" label={previousLabel} onClick={goPrevious} />
            <FlowArrow direction="right" label={nextLabel} onClick={goNext} />
          </>
        )}
      </div>
    </div>
  )
}

interface FlowArrowProps {
  direction: 'left' | 'right'
  label: string
  onClick: () => void
}

function FlowArrow(props: FlowArrowProps) {
  const { direction, label, onClick } = props

  const Icon = direction === 'left' ? ArrowLeft : ArrowRight

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'absolute top-1/2 z-30 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-twin-chat-fg shadow-[0_4px_14px_rgb(0_0_0/0.12)] transition-transform duration-200 hover:-translate-y-[calc(50%+2px)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:bg-twin-chat-surface',
        direction === 'left' ? 'left-0' : 'right-0',
      )}
      data-testid={`twin-cover-flow-${direction === 'left' ? 'previous' : 'next'}`}
    >
      <Icon className="size-4" strokeWidth={1.8} />
    </button>
  )
}

interface CoverCardProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'onSelect'> {
  twin: TwinCoverFlowTwin
  index: number
  isActive: boolean
  belowName?: ReactNode
  onSelect: () => void
}

// forwardRef + spread: a hover-card trigger (Radix asChild) hands the card its ref and handlers.
const CoverCard = forwardRef<HTMLButtonElement, CoverCardProps>(function CoverCard(props, ref) {
  const { twin, index, isActive, belowName, onSelect, onClick, ...triggerProps } = props

  // The card is larger than the 256px thumbnail, so it loads the original portrait.
  const [hasImageFailed, setHasImageFailed] = useState(false)

  const showImage = Boolean(twin.imageUrl) && !hasImageFailed

  return (
    <button
      type="button"
      {...triggerProps}
      ref={ref}
      onClick={(event) => {
        onSelect()
        onClick?.(event)
      }}
      aria-label={twin.name}
      aria-current={isActive ? 'true' : undefined}
      className="group relative block overflow-hidden rounded-2xl text-left transition-shadow duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
      style={{
        width: CARD_WIDTH_PX,
        height: CARD_HEIGHT_PX,
        boxShadow: isActive ? 'var(--home-card-shadow-active)' : 'var(--home-card-shadow)',
      }}
      data-testid={`twin-cover-flow-card-${index}`}
    >
      {showImage ? (
        <img
          src={twin.imageUrl ?? undefined}
          alt=""
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          onError={() => setHasImageFailed(true)}
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center bg-twin-chat-bubble/30">
          <UserAvatar initials={getUserInitials(twin.name)} name={twin.name} size="lg" />
        </span>
      )}

      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: 'linear-gradient(0deg, rgb(0 0 0 / 0.72) 0%, rgb(0 0 0 / 0.12) 42%, rgb(0 0 0 / 0) 62%)' }}
      />

      <span className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-1 p-3">
        <span
          aria-hidden="true"
          className="max-w-full truncate font-display text-xl leading-tight text-white"
          data-testid={`twin-cover-flow-name-${index}`}
        >
          {getFirstName(twin.name)}
        </span>
        {belowName}
      </span>
    </button>
  )
})
